import { morph } from '@theme/morph';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Magniberg product page actions.
 *
 * Two elements share the cart logic in this file:
 * - <mb-product-cta>  (blocks/mb-product-cta.liquid): Notify me, Make an inquiry and
 *   Adjust quantity in place of Add to cart.
 * - <mb-product-set>  (blocks/mb-product-spotlight.liquid): "Complete the set" pop-ups
 *   where a size of a related product is added to the cart.
 *
 * Both re-render themselves from the product section when Horizon announces a cart
 * update, and announce their own cart changes with Horizon's CartLinesUpdateEvent, so
 * the header count and the cart drawer stay in sync.
 */

const KLAVIYO_BACK_IN_STOCK_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2024-02-15';

/** Section ids of the cart sections on the page, so one response can update them. */
function cartSectionIds() {
  const ids = new Set();
  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) ids.add(component.dataset.sectionId);
  }
  return Array.from(ids).join(',');
}

/**
 * Sets the quantity of a cart line and announces it.
 * @param {HTMLElement} source - Element the cart event is dispatched from
 * @param {string} lineKey
 * @param {number} quantity
 */
async function changeLine(source, lineKey, quantity) {
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'product',
      lines: [{ id: lineKey, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: cartSectionIds(),
      sections_url: window.location.pathname,
    });
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const cart = await response.json();

    if (!response.ok || cart.errors) throw new Error(cart.errors || cart.description || 'Cart update failed');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections ?? undefined,
        items: cart.items,
        itemCount: Number(cart.item_count) || 0,
        source: source.localName,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

/**
 * Adds one unit of a variant to the cart and announces it.
 * @param {HTMLElement} source - Element the cart event is dispatched from
 * @param {string} variantId
 */
async function addVariant(source, variantId) {
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: cartSectionIds(),
      sections_url: window.location.pathname,
    });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const added = await response.json();

    if (!response.ok || added.status) throw new Error(added.description || added.message || 'Add to cart failed');

    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: added.sections ?? undefined,
        items: cart.items,
        itemCount: Number(cart.item_count) || 0,
        source: source.localName,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

/** @type {Map<string, Promise<Document | null>>} */
const sectionRequests = new Map();

/**
 * Fetches a rendered section. Concurrent requests for the same URL share one fetch.
 * @param {URL} url
 * @returns {Promise<Document | null>}
 */
function fetchSection(url) {
  const key = url.toString();
  let request = sectionRequests.get(key);

  if (!request) {
    request = fetch(url)
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => (text ? new DOMParser().parseFromString(text, 'text/html') : null))
      .catch(() => null)
      .finally(() => sectionRequests.delete(key));
    sectionRequests.set(key, request);
  }

  return request;
}

/**
 * Base for elements that re-render from the product section and change the cart.
 */
class MbProductElement extends HTMLElement {
  /** @type {AbortController | undefined} */
  abortController;

  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('click', this.onClick, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '') ?? '';
  }

  /** The variant currently selected in this section's product form. */
  get variantId() {
    const input = this.closest('.shopify-section')?.querySelector('product-form-component input[name="id"]');
    return (input instanceof HTMLInputElement && input.value) || this.dataset.variantId || '';
  }

  /**
   * Morphs this element from freshly rendered HTML.
   * @param {Element | Document | null | undefined} html
   */
  update(html) {
    const next = html?.querySelector(this.localName);
    if (!(next instanceof HTMLElement)) return;

    morph(this, next);
    // The morph reconciles the contents; carry the element's own data over too.
    for (const [name, value] of Object.entries(next.dataset)) {
      if (value !== undefined) this.dataset[name] = value;
    }
  }

  /** Re-renders this element from the section for the selected variant. */
  async refresh() {
    if (!this.sectionId) return;

    const url = new URL(window.location.pathname, window.location.origin);
    if (this.variantId) url.searchParams.set('variant', this.variantId);
    url.searchParams.set('section_id', this.sectionId);

    this.update(await fetchSection(url));
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  onCartUpdate = async (event) => {
    try {
      await event.promise;
    } catch {
      // The cart request failed; re-render anyway so the state matches the real cart.
    }

    await this.refresh();
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const quantityButton = event.target.closest('.mb-quantity__button');
    if (quantityButton instanceof HTMLButtonElement && !quantityButton.disabled) {
      const { lineKey, quantity } = quantityButton.dataset;
      if (lineKey && quantity !== undefined) this.run(() => changeLine(this, lineKey, Math.max(0, Number(quantity))));
      return;
    }

    this.onOtherClick(event.target);
  };

  /**
   * Hook for clicks that are not quantity buttons.
   * @param {Element} _target
   */
  onOtherClick(_target) {}

  /**
   * Runs a cart action with the element marked busy.
   * @param {() => Promise<void>} action
   */
  async run(action) {
    this.setAttribute('aria-busy', 'true');
    try {
      await action();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

/**
 * Notify me / Make an inquiry / Adjust quantity. Also follows the selected variant and
 * sends back-in-stock sign-ups to Klaviyo's client API.
 */
class MbProductCta extends MbProductElement {
  connectedCallback() {
    super.connectedCallback();
    const signal = this.abortController?.signal;

    this.addEventListener('submit', this.#onSubmit, { signal });
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
  }

  /** @param {Event & { promise?: Promise<{ detail?: { html?: Document | Element } }> }} event */
  #onProductSelect = async (event) => {
    // Ignore variant changes made in product cards or other sections.
    const source = event.target instanceof Element ? event.target : null;
    if (source?.closest('product-card, quick-add-dialog')) return;
    if (source && this.closest('.shopify-section') !== source.closest('.shopify-section')) return;

    try {
      const result = await event.promise;
      this.update(result?.detail?.html);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') console.warn('[mb-product-cta]', error);
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.mb-product-cta__form')) return;

    event.preventDefault();

    const email = new FormData(form).get('email');
    const { companyId, variantId } = form.dataset;
    const message = this.querySelector('.mb-product-cta__message');
    if (typeof email !== 'string' || !email || !companyId || !variantId) return;

    form.setAttribute('aria-busy', 'true');

    let ok = false;
    try {
      const response = await fetch(`${KLAVIYO_BACK_IN_STOCK_URL}?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: {
          revision: KLAVIYO_REVISION,
          'Content-Type': 'application/json',
          'X-Klaviyo-Onsite': '1',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
            },
          },
        }),
      });
      ok = response.ok;
    } catch {
      ok = false;
    }

    form.removeAttribute('aria-busy');

    if (message instanceof HTMLElement) {
      message.textContent = (ok ? message.dataset.success : message.dataset.error) ?? '';
      message.hidden = false;
    }

    if (ok) form.reset();
  };
}

/**
 * "Complete the set": each related product opens a pop-up with a size list that adds
 * straight to the cart, or quantity controls once it is in the cart.
 */
class MbProductSet extends MbProductElement {
  /**
   * @param {Element} target
   */
  onOtherClick(target) {
    const toggle = target.closest('.mb-set__select');
    if (toggle instanceof HTMLElement) {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      return;
    }

    const add = target.closest('.mb-set__size[data-variant-id]');
    if (add instanceof HTMLElement && add.dataset.variantId) {
      const variantId = add.dataset.variantId;
      this.run(() => addVariant(this, variantId));
    }
  }
}

if (!customElements.get('mb-product-cta')) {
  customElements.define('mb-product-cta', MbProductCta);
}

if (!customElements.get('mb-product-set')) {
  customElements.define('mb-product-set', MbProductSet);
}
